import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';
import { rememberCompany } from '@/lib/companySetup';
import { resetCompanyInfoCache } from '@/lib/useCompanyInfo';

/**
 * The company's currency and VAT rate (SellGo's own database) — in Settings,
 * and as the first step of the first-time setup.
 */

// Saudi first, then Egypt. Currencies with 2 decimals: amounts are kept to 2.
export const CURRENCIES = [
  { code: 'SAR', name: 'ريال سعودي', symbol: 'ر.س', country: 'السعودية', vat: 0.15, timezone: 'Asia/Riyadh' },
  { code: 'EGP', name: 'جنيه مصري', symbol: 'ج.م', country: 'مصر', vat: 0.14, timezone: 'Africa/Cairo' },
  { code: 'AED', name: 'درهم إماراتي', symbol: 'د.إ', country: 'الإمارات', vat: 0.05, timezone: 'Asia/Dubai' },
  { code: 'QAR', name: 'ريال قطري', symbol: 'ر.ق', country: 'قطر', vat: 0, timezone: 'Asia/Qatar' },
  { code: 'USD', name: 'دولار أمريكي', symbol: '$', country: '', vat: 0, timezone: null },
];

const toPercent = (rate) => String(Math.round(Number(rate || 0) * 10000) / 100);
const sample = (symbol) => `${(1250).toLocaleString('ar-EG', { minimumFractionDigits: 2 })} ${symbol}`;

// firstSetup: also ticks the currency's usual payment methods.
// preselect: start from the company's current currency (the first setup makes
// the manager choose, until they have).
export default function CurrencyForm({ company, firstSetup = false, preselect = true, submitLabel = 'حفظ', onSaved }) {
  const known = CURRENCIES.find(c => c.code === company?.currency);
  const [code, setCode] = useState(preselect ? (known ? known.code : 'OTHER') : null);
  const [other, setOther] = useState({
    code: known ? '' : (company?.currency || ''),
    symbol: known ? '' : (company?.currency_symbol || ''),
  });
  const [vat, setVat] = useState(preselect ? toPercent(company?.vat_rate) : '');
  const [saving, setSaving] = useState(false);

  const preset = CURRENCIES.find(c => c.code === code);
  const chosen = code === 'OTHER'
    ? { code: other.code.trim().toUpperCase(), symbol: other.symbol.trim(), timezone: null }
    : preset;

  const pick = (c) => { setCode(c.code); setVat(toPercent(c.vat)); };

  const save = async () => {
    if (!chosen) { toast.error('اختار عملة الشركة'); return; }
    if (code === 'OTHER' && !/^[A-Z]{3}$/.test(chosen.code)) { toast.error('الرمز الدولي للعملة 3 حروف إنجليزي، مثال: OMR'); return; }
    if (code === 'OTHER' && !chosen.symbol) { toast.error('اكتب رمز العملة اللي يظهر جنب المبالغ'); return; }
    const pct = vat.trim() === '' ? NaN : Number(vat);
    if (!(pct >= 0 && pct <= 100)) { toast.error('نسبة الضريبة لازم تكون من 0 لـ 100'); return; }

    setSaving(true);
    try {
      const res = await api.post('/sellgo_company_settings', {
        currency: chosen.code,
        currency_symbol: chosen.symbol,
        vat_rate: pct / 100,
        ...(chosen.timezone ? { timezone: chosen.timezone } : {}),
        ...(firstSetup ? { payment_defaults: true } : {}),
      });
      rememberCompany(res.data);
      resetCompanyInfoCache();
      if (!firstSetup) toast.success('تم حفظ العملة والضريبة');
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.message || 'تعذّر الحفظ');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-bold text-foreground">عملة الشركة</p>
        <div className="grid grid-cols-2 gap-2">
          {CURRENCIES.map(c => (
            <button key={c.code} type="button" onClick={() => pick(c)}
              className={`rounded-2xl border-2 p-3 text-right transition-all ${code === c.code ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'}`}>
              <span className="flex items-center justify-between gap-2">
                <span className={`text-sm font-bold ${code === c.code ? 'text-primary' : 'text-foreground'}`}>{c.name}</span>
                <span className="text-sm font-black text-muted-foreground">{c.symbol}</span>
              </span>
              <span className="block text-[11px] text-muted-foreground mt-0.5">{c.country ? `${c.country} · ` : ''}{c.code}</span>
            </button>
          ))}
          <button type="button" onClick={() => setCode('OTHER')}
            className={`rounded-2xl border-2 p-3 text-right transition-all ${code === 'OTHER' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40'}`}>
            <span className={`block text-sm font-bold ${code === 'OTHER' ? 'text-primary' : 'text-foreground'}`}>عملة أخرى</span>
            <span className="block text-[11px] text-muted-foreground mt-0.5">اكتب رمزها بنفسك</span>
          </button>
        </div>

        {code === 'OTHER' && (
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">الرمز الدولي</label>
                <Input value={other.code} onChange={e => setOther(o => ({ ...o, code: e.target.value.toUpperCase() }))}
                  placeholder="OMR" maxLength={3} dir="ltr" className="mt-1 rounded-xl" />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">يظهر جنب المبالغ</label>
                <Input value={other.symbol} onChange={e => setOther(o => ({ ...o, symbol: e.target.value }))}
                  placeholder="ر.ع" maxLength={8} className="mt-1 rounded-xl" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">المبالغ بتتحسب لحد رقمين بعد العلامة العشرية.</p>
          </div>
        )}
      </div>

      <div>
        <label className="text-sm font-bold text-foreground">نسبة ضريبة القيمة المضافة (%)</label>
        <Input value={vat} onChange={e => setVat(e.target.value)} type="number" min="0" max="100" step="any"
          inputMode="decimal" placeholder="15" className="mt-1 rounded-xl" />
        <p className="text-xs text-muted-foreground mt-1">
          {preset?.country ? `النسبة في ${preset.country}: ${toPercent(preset.vat)}% — ` : ''}اكتب 0 لو الشركة مش مسجّلة في الضريبة.
        </p>
      </div>

      {chosen?.symbol && (
        <div className="bg-muted/50 rounded-xl px-3 py-2 flex justify-between items-center">
          <span className="text-xs text-muted-foreground">المبالغ هتظهر كده</span>
          <span className="text-sm font-bold text-primary">{sample(chosen.symbol)}</span>
        </div>
      )}

      {!firstSetup && (
        <p className="text-xs text-muted-foreground leading-relaxed">
          تغيير العملة بيغيّر الرمز اللي جنب المبالغ بس — الأسعار والمبالغ المسجلة مش بتتحوّل لعملة تانية.
          والضريبة الجديدة بتتطبّق على الطلبات الجديدة.
        </p>
      )}

      <Button onClick={save} disabled={saving || !chosen} className="w-full bg-primary h-11 rounded-xl gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {submitLabel}
      </Button>
    </div>
  );
}
